import { backgroundTransitionOnClick, Divider, IconFont, pointer, singleTextEllipsis, Text, Tooltip } from '@byecode/ui'
import { AppUserStatus } from '@lighthouse/core'
import type { UserOption } from '@lighthouse/shared'
import {
    getColorById,
    getImageFullUrlInApplication,
    PersonSelect,
    TooltipText,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    UserAvatar
} from '@lighthouse/shared'
import { clone, find } from 'rambda'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { setAppRoleAtom } from '@/atoms/application/action'
import { commonRoleIds, developerRole, visitorRole } from '@/atoms/application/constants'
import { appRoleAtom } from '@/atoms/application/state'
import { useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceDepartments, useDataSourceRoles, useDataSourceUserDetails, useDataSourceUsers } from '@/hooks/useDataSource'

import * as SC from './styles'

interface SelectRoleProps {}

const EMPTY_ITEM_EXTRA: { role?: string; department?: string } = {}

function readToken(item: unknown): { id?: string; name?: string } {
    if (typeof item === 'string' || typeof item === 'number') {
        const id = String(item).trim()
        return id ? { id } : {}
    }
    if (!item || typeof item !== 'object') {
        return {}
    }
    const record = item as Record<string, unknown>
    const rawId = record.id ?? record.value ?? record.departmentId
    const rawName = record.name ?? record.label ?? record.departmentName
    const id = typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId).trim() : ''
    const name = typeof rawName === 'string' ? rawName.trim() : ''
    return { id: id || undefined, name: name || undefined }
}

function toNames(value: unknown, nameById: Map<string, string>, knownNames?: Set<string>) {
    const items = Array.isArray(value) ? value : value == null || value === '' ? [] : [value]
    const names: string[] = []
    const seen = new Set<string>()
    const push = (name?: string) => {
        if (!name || seen.has(name)) {
            return
        }
        seen.add(name)
        names.push(name)
    }
    const resolve = (token: { id?: string; name?: string }) => {
        if (token.id && nameById.has(token.id)) {
            return nameById.get(token.id)
        }
        if (token.id && knownNames?.has(token.id)) {
            return token.id
        }
        return token.name
    }
    for (const item of items) {
        const token = readToken(item)
        if (token.id && !nameById.has(token.id) && !knownNames?.has(token.id) && /[,、]/.test(token.id)) {
            for (const part of token.id.split(/[,、]/u)) {
                push(resolve(readToken(part.trim())))
            }
            continue
        }
        push(resolve(token))
    }
    return names.join('、')
}

const SCxIconWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    align-items: center;
    height: 22px;
`

const SCxLeftFill = styled.div`
    display: flex;
    align-items: center;

    > * {
        margin-right: 4px;
    }
`

const SCxUserContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 40%;
    /* max-width: calc(100% - 30% - 356px); */
    overflow: hidden;
    gap: 4px;
    white-space: nowrap;

    /* background-color: #fff; */
`
const SCxRoleContainer = styled.div`
    display: inline-flex;
    padding: 4px;
    height: 32px;
    align-items: center;
    border-radius: 6px;
    overflow: hidden;
    ${backgroundTransitionOnClick}
`
const SCxTargetContainer = styled.span`
    ${singleTextEllipsis}
    line-height: 22px;
`

const SCxDropDownItem = styled.div`
    height: 40px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    ${backgroundTransitionOnClick}
    ${pointer}
`

export const SelectRole: React.FunctionComponent<SelectRoleProps> = () => {
    const userId = useAtomData(appRoleAtom)
    const envId = useCurrentEnvId()
    const roles = useDataSourceRoles()
    const departments = useDataSourceDepartments()
    const users = useDataSourceUsers()
    const userDetails = useDataSourceUserDetails()
    const itemExtraById = useMemo(() => {
        const roleNameById = new Map(roles.map(role => [role.id, role.name]))
        const departmentNameById = new Map<string, string>()
        const departmentNames = new Set<string>()
        for (const department of departments) {
            if (department.id) {
                departmentNameById.set(department.id, department.name)
            }
            if (department.recordId && department.recordId !== department.id) {
                departmentNameById.set(department.recordId, department.name)
            }
            if (department.name) {
                departmentNames.add(department.name)
            }
        }
        const extras = new Map<string, { role?: string; department?: string }>()
        for (const user of userDetails) {
            const id = String(user.userId ?? '')
            if (!id) {
                continue
            }
            const role = toNames(user.roleId, roleNameById)
            const department = toNames(user.departmentIds, departmentNameById, departmentNames)
            if (!role && !department) {
                continue
            }
            extras.set(id, {
                role: role || undefined,
                department: department || undefined
            })
        }
        return extras
    }, [departments, roles, userDetails])
    const getItemExtra = useCallback((user: UserOption) => itemExtraById.get(user.id) ?? EMPTY_ITEM_EXTRA, [itemExtraById])
    const { run: setAppRole } = useAtomAction(setAppRoleAtom)
    const { appId } = useApplicationContext()
    /** 获取当前用户记录 */
    const currentOptions = useMemo(() => clone(users).filter(item => item.status !== AppUserStatus.DEPART), [users])
    const developerOption = useMemo<UserOption>(() => ({ id: developerRole.userId, name: developerRole.username }), [])
    const visitorOption = useMemo<UserOption>(() => ({ id: visitorRole.userId, name: visitorRole.username }), [])

    const user = useMemo(() => {
        if (userId === developerRole.userId) {
            return developerOption
        }
        if (userId === visitorRole.userId) {
            return visitorOption
        }
        return find(item => item.id === userId, currentOptions)
    }, [currentOptions, developerOption, userId, visitorOption])

    const renderAvatar = useMemo(() => {
        if (user?.id === developerRole.userId) {
            return <SC.RoundIcon type="Wrench" size={16} />
        }
        if (user?.id === visitorRole.userId) {
            return <SC.RoundIcon type="User" size={16} />
        }
        return (
            <UserAvatar
                id={user?.id ?? ''}
                avatar={getImageFullUrlInApplication(appId, user?.avatar)}
                style={{ flexShrink: 0 }}
                color="var(--color-white)"
                background={getColorById(user?.id ?? '')}
                displayHover
                name={user?.name ?? '未命名用户'}
                size={24}
            />
        )
    }, [appId, user?.avatar, user?.id, user?.name])

    const renderValue = useMemo(() => {
        return (
            <>
                <div style={{ marginRight: 8 }}>{renderAvatar}</div>
                <TooltipText
                    title={user?.name ?? '未命名用户'}
                    render={ref => {
                        return <SCxTargetContainer ref={ref}>{user?.name ?? '未命名用户'}</SCxTargetContainer>
                    }}
                />
                <SCxIconWrapper>
                    <IconFont type="ArrowDownSmall" size={16} color="var(--color-gray-400)" />
                </SCxIconWrapper>
            </>
        )
    }, [renderAvatar, user?.name])

    useEffect(() => {
        if (!user) {
            setAppRole(developerRole.userId)
        }
    }, [setAppRole, user])

    const renderPrefixExtra = useCallback(
        (onClose?: () => void) => {
            return (
                <>
                    <SCxDropDownItem
                        onClick={() => {
                            onClose?.()
                            setAppRole(developerRole.userId)
                        }}
                    >
                        <SCxLeftFill>
                            <SC.RoundIcon type="Wrench" size={16} />
                            <Text>{developerRole.username}</Text>
                        </SCxLeftFill>
                        <Tooltip title="应用管理者不受权限约束" disablePortal>
                            <SC.Icon type="Question" color="var(--color-gray-400)" size={18} />
                        </Tooltip>
                    </SCxDropDownItem>
                    <SCxDropDownItem
                        onClick={() => {
                            onClose?.()
                            setAppRole(visitorRole.userId)
                        }}
                    >
                        <SCxLeftFill>
                            <SC.RoundIcon type="User" size={16} />
                            <Text>{visitorRole.username}</Text>
                        </SCxLeftFill>
                        <Tooltip title="游客是未登录的用户" disablePortal>
                            <SC.Icon type="Question" color="var(--color-gray-400)" size={18} />
                        </Tooltip>
                    </SCxDropDownItem>
                    <Divider style={{ margin: '8px 0 0 0' }} color="var(--color-gray-200)" />
                </>
            )
        },
        [setAppRole]
    )

    return (
        <SCxUserContainer>
            <SCxRoleContainer>
                <PersonSelect
                    valueComponent={renderValue}
                    extraComponent={({ onClose }) => renderPrefixExtra(onClose)}
                    options={currentOptions}
                    value={[user?.id ?? '']}
                    withinPortal
                    isMultiple={false}
                    enableCheckBox={false}
                    placeholder="成员名称、角色、部门"
                    styles={{
                        container: {
                            // width: 'calc(100% - 16px)',
                            borderStyle: 'none',
                            overflow: 'hidden'
                        },
                        value: {
                            padding: '0 4px'
                        }
                    }}
                    minWidth={300}
                    roleOptions={roles}
                    getItemExtra={getItemExtra}
                    onChange={val => {
                        const roleId = val[0]
                        setAppRole(roleId)
                    }}
                />
            </SCxRoleContainer>
            <Text color="var(--color-gray-400)">的视角</Text>
        </SCxUserContainer>
    )
}
